import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { isAuthenticated, hasRole, getUserRole, redirectToLogin } from '../../utils/auth';

/**
 * A protected route component that checks for authentication and role-based access
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components to render if authenticated
 * @param {string|string[]} props.allowedRoles - Roles that are allowed to access this route
 * @param {string} props.redirectPath - Path to redirect to if not authenticated or not authorized
 * @returns {React.ReactNode} - The protected component or redirect
 */
const RoleBasedRoute = ({ 
  children, 
  allowedRoles, 
  redirectPath = '/login' 
}) => {
  const [isChecking, setIsChecking] = useState(true);
  const [isAuthorized, setIsAuthorized] = useState(false);
  const location = useLocation();

  useEffect(() => {
    // Check if user is authenticated
    const authenticated = isAuthenticated();
    
    // If not authenticated, we're done checking
    if (!authenticated) {
      setIsChecking(false);
      return;
    }
    
    // If allowedRoles is not provided, just check for authentication
    if (!allowedRoles) {
      setIsAuthorized(authenticated);
      setIsChecking(false);
      return;
    }
    
    // Check if user has the required role
    const authorized = hasRole(allowedRoles);
    setIsAuthorized(authorized);
    setIsChecking(false);
  }, [allowedRoles, location.pathname]);

  // Show nothing while checking
  if (isChecking) {
    return null;
  }

  // If not authenticated, redirect to login
  if (!isAuthenticated()) {
    // Determine the appropriate login page based on the current path
    let loginPath = redirectPath;
    
    // Store the current path for redirection after login
    const currentPath = location.pathname + location.search;
    localStorage.setItem('redirectPath', currentPath);
    
    return <Navigate to={loginPath} replace state={{ from: location }} />;
  }

  // If not authorized for this role, redirect to appropriate dashboard
  if (!isAuthorized) {
    const role = getUserRole();
    let dashboardPath = '/';
    
    // Determine dashboard based on role
    switch (role?.toLowerCase()) {
      case 'superadmin':
      case 'subadmin':
        dashboardPath = '/admin/dashboard';
        break;
      case 'reporter':
        dashboardPath = '/reporter/dashboard';
        break;
      case 'advertiser':
        dashboardPath = '/advertiser/dashboard';
        break;
      case 'influencer':
        dashboardPath = '/influencer/dashboard';
        break;
      case 'ryvuser':
        dashboardPath = '/raise/dashboard';
        break;
      case 'press':
        dashboardPath = '/press/dashboard';
        break;
      case 'podcastuser':
        dashboardPath = '/podcast';
        break;
      default:
        dashboardPath = '/';
    }
    
    return <Navigate to={dashboardPath} replace />;
  }

  // If authenticated and authorized, render children
  return children;
};

export default RoleBasedRoute;
